import React, { FC, ReactNode } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useGetScalingSize } from '@ui/Report';
import { REPORT_PAGE_TITLE } from './editable-page-header.constants';

type ReportPageHeaderProps = {
  title: ReactNode;
  children?: ReactNode;
  /**
   * Figma page-edge offsets. Turn off when {@link Report.Page} already applies
   * padding — otherwise title and footer insets stack.
   */
  edgeInset?: boolean;
  /** Small label after the title, e.g. `1/2` when a slide is split. */
  titleSuffix?: ReactNode;
};

/**
 * Report page title block — Figma node 4767:41120. Position and typography
 * scale relative to page width via cqw units.
 */
export const ReportPageHeader: FC<ReportPageHeaderProps> = ({
  title,
  children,
  edgeInset = true,
  titleSuffix,
}) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useStyles({ getScalingSize, edgeInset });

  return (
    <Box className={classes.root}>
      <Box component="h1" className={classes.title}>
        {title}
        {titleSuffix ? (
          <Box component="span" className={classes.titleSuffix}>
            {titleSuffix}
          </Box>
        ) : null}
      </Box>
      {children}
    </Box>
  );
};

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
  edgeInset: boolean;
}>()((_theme, { getScalingSize, edgeInset }) => ({
  root: {
    position: 'relative',
    zIndex: 10,
    flexShrink: 0,
    overflow: 'visible',
    paddingTop: edgeInset ? getScalingSize(REPORT_PAGE_TITLE.offsetTop) : 0,
    paddingRight: edgeInset ? getScalingSize(REPORT_PAGE_TITLE.offsetLeft) : 0,
    paddingBottom: 0,
    paddingLeft: edgeInset ? getScalingSize(REPORT_PAGE_TITLE.offsetLeft) : 0,
  },
  title: {
    margin: 0,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontFamily: REPORT_PAGE_TITLE.fontFamily,
    fontSize: getScalingSize(REPORT_PAGE_TITLE.fontSize),
    fontWeight: REPORT_PAGE_TITLE.fontWeight,
    lineHeight: REPORT_PAGE_TITLE.lineHeight,
    color: REPORT_PAGE_TITLE.color,
    whiteSpace: 'nowrap',
  },
  titleSuffix: {
    display: 'inline',
    marginLeft: getScalingSize(1),
    verticalAlign: 'baseline',
    fontWeight: 400,
    lineHeight: REPORT_PAGE_TITLE.lineHeight,
    '& > *': {
      display: 'inline',
      verticalAlign: 'baseline',
      lineHeight: 'inherit',
    },
  },
}));
